import moment from "moment-timezone";

/** Shape swagger reflects for .NET's DateOnly struct. */
type DateOnlyParts = { year?: number; month?: number; day?: number };

/**
 * Extract a timezone-safe YYYY-MM-DD string from any date/datetime value.
 * Uses UTC methods so the result is the same regardless of machine timezone.
 * For bare date strings ("1956-09-24") they pass through unchanged.
 * For datetime strings ("1956-09-23T22:00:00.000Z") the UTC date is used.
 *
 * ponytail: also accepts the {year,month,day} object because swagger types
 * DateOnly as that struct while System.Text.Json actually sends "1956-09-24".
 * Cheaper to handle both than to trust either.
 */
export function dateOnlyISO(dateStr: string | DateOnlyParts | null | undefined): string {
  if (!dateStr) return '';
  if (typeof dateStr === 'object') {
    const { year, month, day } = dateStr;
    if (!year || !month || !day) return '';
    return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  } catch { return ''; }
}

/**
 * Format a DOB string as DD/MM/YYYY (en-GB) without timezone shifting.
 */
export function formatDOBEnGB(dateStr: string | null | undefined): string {
  const iso = dateOnlyISO(dateStr);
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * The one storage format for a date of birth: a bare ISO calendar date,
 * "YYYY-MM-DD". No time, no timezone, nothing that can shift a day.
 *
 * It used to append "T00:00:00.000Z". That was the drift: a date of birth has
 * no time, so any time we attach is invented, and every consumer that formats
 * it in local time can land on the previous day. JavaScript has no date-only
 * type — a Date is a UTC instant rendered locally, which is worse, not better —
 * so the ISO date string IS the correct representation here. It is also what
 * <input type="date"> reads and writes, and what the API's DateOnly expects.
 *
 * Enforced at the repo boundary (see patientRepo), so no caller can store
 * anything else.
 */
export function normalizeDOBForStorage(dateStr: string | null | undefined): string | null {
  return dateOnlyISO(dateStr) || null;
}

export function formatDateShort(date: Date | string) {
  if (!date) return "";
  return moment(date).format("DD-MM-YYYY");
}

export function formatDateLong(date: Date | string, timezone?: Timezone) {
  if (!date) return "";
  return moment(parseDateWithTimeZone(date, timezone)).format("DD MMMM YYYY");
}

export function formatTimeShort(date: Date | string, timezone?: Timezone) {
  if (!date) return "";
  return moment(parseDateWithTimeZone(date, timezone)).format("HH:mm");
}

export function formatTimeShortWithoutTimeZone(date: Date | string) {
  if (!date) return "";
  return moment(date).format("HH:mm");
}

export function formatTimeShortA(date: Date | string, timezone?: Timezone) {
  if (!date) return "";
  return moment(parseDateWithTimeZone(date, timezone)).format("h:mm A");
}

export function formatDateTimeShort(date: Date | string, timezone?: Timezone) {
  if (!date) return "";
  return moment(parseDateWithTimeZone(date, timezone)).format(
    "DD-MM-YYYY HH:mm"
  );
}

export function formatDateTimeShortWithoutTimeZone(date: Date | string) {
  if (!date) return "";
  return moment(date).format("DD-MM-YYYY HH:mm");
}

/**
 * Enum for commonly used timezones
 * This includes major timezones from different regions
 */
export enum Timezone {
  // North America
  EASTERN = "America/New_York",
  CENTRAL = "America/Chicago",
  MOUNTAIN = "America/Denver",
  PACIFIC = "America/Los_Angeles",
  ALASKA = "America/Anchorage",
  HAWAII = "Pacific/Honolulu",

  // Europe
  LONDON = "Europe/London",
  PARIS = "Europe/Paris",
  BERLIN = "Europe/Berlin",
  MOSCOW = "Europe/Moscow",

  // Asia
  DUBAI = "Asia/Dubai",
  SINGAPORE = "Asia/Singapore",
  TOKYO = "Asia/Tokyo",
  HONG_KONG = "Asia/Hong_Kong",
  SHANGHAI = "Asia/Shanghai",

  // Oceania
  SYDNEY = "Australia/Sydney",
  MELBOURNE = "Australia/Melbourne",
  AUCKLAND = "Pacific/Auckland",

  // South America
  SAO_PAULO = "America/Sao_Paulo",
  BUENOS_AIRES = "America/Argentina/Buenos_Aires",

  // Africa
  SOUTH_AFRICA = "Africa/Johannesburg",

  // India
  INDIA = "Asia/Kolkata",

  UTC = "UTC",
}

/**
 * Validates whether a value is a valid timezone from the Timezone enum.
 * @param {string | Timezone} value - Value to validate.
 * @returns {boolean} True if valid, false otherwise.
 */
export function isValidTimezone(value: string | Timezone): boolean {
  const timezones = Object.values(Timezone);
  return timezones.includes(value as Timezone);
}

/**
 * Gets the timezone string from the Timezone enum or defaults to the system's timezone.
 * @param {Timezone | undefined} timezone - Optional timezone.
 * @returns {string} The timezone string.
 */
function getTimezoneString(timezone?: Timezone): string {
  if (!timezone || isValidTimezone(timezone) === false) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || Timezone.UTC;
  }

  return timezone;
}

/**
 * Converts a UTC or ISO date string to a Date object in the specified timezone.
 * @param {string | Date} dateInput - The input date (UTC or ISO string).
 * @param {Timezone} timezone - Target timezone for conversion.
 * @returns {Date} A Date object adjusted to the target timezone.
 */
export function parseDateWithTimeZone(
  date: string | Date,
  timezone: Timezone = Timezone.LONDON
): Date {
  if (!date) {
    return undefined!;
  }

  const timeZoneString = getTimezoneString(timezone);

  const result = new Date(
    (typeof date === "string" ? new Date(date) : date).toLocaleString("en-US", {
      timeZone: timeZoneString,
    })
  );
  return result;
}
